/* PO Receive Panel — Goods Receipt Note (GRN) entry + 3-way match preview.
 *
 * Wired endpoints:
 *   GET  /api/purchase-orders/:id                  — header
 *   GET  /api/purchase-orders/:id/match-preview    — ordered vs received per line
 *   GET  /api/purchase-orders/:id/grns             — GRN history
 *   POST /api/purchase-orders/:id/grns             — draft GRN
 *   POST /api/grns/:id/post                        — lock + update Item Master
 *   GET  /api/purchase-orders/:id/integrity        — chain hash verifier
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Package, Truck, CheckCircle2, Lock, ShieldCheck, AlertTriangle,
  ArrowLeft, FileText, RefreshCw,
} from 'lucide-react';


const recvTone = {
  not_received:   'bg-gray-100 text-gray-700',
  partial:        'bg-amber-100 text-amber-800',
  fully_received: 'bg-emerald-100 text-emerald-800',
};


export default function POReceivePanel() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo]                 = useState(null);
  const [preview, setPreview]       = useState(null);
  const [history, setHistory]       = useState([]);
  const [integrity, setIntegrity]   = useState(null);
  const [draft, setDraft]           = useState({});  // line_index → qty
  const [deliveryNoteNo, setDNNo]   = useState('');
  const [busy, setBusy]             = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [poR, prevR, histR, intR] = await Promise.all([
        axiosInstance.get(`/purchase-orders/${id}`),
        axiosInstance.get(`/purchase-orders/${id}/match-preview`),
        axiosInstance.get(`/purchase-orders/${id}/grns`),
        axiosInstance.get(`/purchase-orders/${id}/integrity`).catch(() => null),
      ]);
      setPo(poR.data || null);
      setPreview(prevR.data || null);
      setHistory(histR.data?.grns || []);
      setIntegrity(intR?.data || null);
    } catch (_) { toast.error('Failed to load PO'); }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitDraft = async () => {
    const lines = Object.entries(draft)
      .filter(([, q]) => Number(q) > 0)
      .map(([idx, q]) => ({ po_line_index: Number(idx), received_qty: Number(q) }));
    if (lines.length === 0) {
      toast.error('Enter a received qty for at least one line');
      return;
    }
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/purchase-orders/${id}/grns`, {
        lines, delivery_note_no: deliveryNoteNo || undefined,
      });
      toast.success(`Draft ${r.data?.grn_no} created — posting…`);
      // Auto-post (one-click receive)
      await axiosInstance.post(`/grns/${r.data.id}/post`);
      toast.success(`${r.data?.grn_no} posted · Item Master refreshed`);
      setDraft({});
      setDNNo('');
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.error || d.message)) || 'Receipt failed');
    } finally { setBusy(false); }
  };

  if (!po) {
    return <div className="p-6 text-sm text-gray-500" data-testid="po-receive-loading">Loading…</div>;
  }

  return (
    <div className="p-6 space-y-4" data-testid="po-receive-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <button onClick={() => navigate(po.rfq_id ? `/rfqs/${po.rfq_id}` : '/purchase-orders')}
                  className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-600" /> {po.po_number}
            <span className="text-base font-normal text-gray-600">— {po.supplier_name}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${recvTone[po.received_status] || recvTone.not_received}`}
                  data-testid="po-receive-status">
              {(po.received_status || 'not_received').replace('_', ' ')}
            </span>
          </h1>
          {po.rfq_no && (
            <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-2">
              <FileText className="w-3 h-3" /> Source: <span className="font-mono">{po.rfq_no}</span>
              {po.decision_lock_hash && (
                <span className="inline-flex items-center gap-1 text-violet-700 font-mono"
                      title={po.decision_lock_hash}>
                  <Lock className="w-3 h-3" /> {po.decision_lock_hash.slice(0, 10)}…
                </span>
              )}
            </div>
          )}
        </div>
        <button onClick={refresh} className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Integrity badge */}
      {integrity && (
        <div className={`border rounded-lg px-3 py-2 text-xs flex items-center justify-between gap-2 ${
          integrity.ok ? 'border-emerald-300 bg-emerald-50 text-emerald-900' :
          'border-rose-300 bg-rose-50 text-rose-900'
        }`} data-testid="po-integrity-badge">
          <span className="inline-flex items-center gap-1">
            {integrity.ok ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {integrity.skipped === 'manual_po' ? 'Manual PO (no chain to verify)' :
             integrity.ok ? 'Execution chain verified — no tampering detected' :
             `Integrity FAILURE — ${integrity.error || 'chain mismatch'}`}
          </span>
          {integrity.verified_hash && (
            <span className="font-mono text-[10px]" title={integrity.verified_hash}>
              chain: {integrity.verified_hash.slice(0, 12)}…
            </span>
          )}
        </div>
      )}

      {/* Receipt entry table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-sm flex items-center gap-2">
          <Package className="w-4 h-4" /> Receive Goods
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-600 bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-right px-3 py-2">Ordered</th>
              <th className="text-right px-3 py-2">Already Received</th>
              <th className="text-right px-3 py-2">Remaining</th>
              <th className="text-right px-3 py-2">Unit Price</th>
              <th className="text-right px-3 py-2 w-32">Receiving Now</th>
            </tr>
          </thead>
          <tbody>
            {(preview?.lines || []).map((row) => (
              <tr key={row.po_line_index} className="border-t border-gray-100"
                  data-testid={`po-recv-row-${row.po_line_index}`}>
                <td className="px-3 py-2">
                  <div className="text-sm">{row.description}</div>
                  {row.item_code && <div className="text-[10px] font-mono text-gray-500">{row.item_code}</div>}
                </td>
                <td className="px-3 py-2 text-right font-mono">{row.ordered_qty}</td>
                <td className="px-3 py-2 text-right font-mono">{row.received_qty}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">{row.remaining_qty}</td>
                <td className="px-3 py-2 text-right font-mono">{row.unit_price?.toLocaleString()}</td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number" min="0" max={row.remaining_qty} step="0.01"
                    value={draft[row.po_line_index] || ''}
                    onChange={(e) => setDraft({ ...draft, [row.po_line_index]: e.target.value })}
                    placeholder="0"
                    disabled={row.remaining_qty <= 0}
                    data-testid={`po-recv-input-${row.po_line_index}`}
                    className="w-24 text-right text-sm border border-gray-300 rounded px-2 py-1 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-3 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <label className="text-xs text-gray-700">Delivery Note No.</label>
            <input value={deliveryNoteNo} onChange={(e) => setDNNo(e.target.value)}
                   placeholder="DN-XXXX"
                   data-testid="po-recv-dn-input"
                   className="text-sm border border-gray-300 rounded px-2 py-1 w-40" />
          </div>
          <button onClick={submitDraft} disabled={busy}
                  data-testid="po-recv-submit-button"
                  className="px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Receive & Post GRN
          </button>
        </div>
      </div>

      {/* GRN history */}
      {history.length > 0 && (
        <div className="border border-indigo-200 bg-indigo-50 rounded-lg overflow-hidden"
             data-testid="po-grn-history">
          <div className="px-3 py-2 bg-indigo-100 border-b border-indigo-300 font-semibold text-sm text-indigo-900">
            Receipt History
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-indigo-900 bg-indigo-50">
              <tr>
                <th className="text-left px-3 py-1">GRN No.</th>
                <th className="text-left px-3 py-1">Date</th>
                <th className="text-left px-3 py-1">DN</th>
                <th className="text-right px-3 py-1">Lines</th>
                <th className="text-right px-3 py-1">Subtotal</th>
                <th className="text-left px-3 py-1">Status</th>
                <th className="text-left px-3 py-1">Chain</th>
              </tr>
            </thead>
            <tbody>
              {history.map((g) => (
                <tr key={g.id} className="border-t border-indigo-200"
                    data-testid={`po-grn-row-${g.grn_no}`}>
                  <td className="px-3 py-1 font-mono text-xs">{g.grn_no}</td>
                  <td className="px-3 py-1 text-xs">{g.grn_date}</td>
                  <td className="px-3 py-1 text-xs">{g.delivery_note_no || '—'}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{(g.lines || []).length}</td>
                  <td className="px-3 py-1 text-right font-mono text-xs">{g.subtotal?.toLocaleString()}</td>
                  <td className="px-3 py-1 text-xs">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      g.status === 'posted' ? 'bg-emerald-100 text-emerald-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>{g.status}</span>
                  </td>
                  <td className="px-3 py-1 font-mono text-[10px] text-violet-700"
                      title={g.execution_chain_hash}>
                    {g.execution_chain_hash?.slice(0, 10)}…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
